/**
 * @fileoverview Enterprise File Operations & Archive Manager
 * @module MikhmelViewer/FileOps
 *
 * Handles DICOM parsing (Worker/Main thread), IDB Archive management, 
 * Anonymization logic, and QR/URL sharing.
 *
 * Enterprise Features:
 *   ✓ AbortController integration for robust load cancellation.
 *   ✓ Safe Binary Validation (Magic Bytes "DICM" check).
 *   ✓ Event Delegation for Archive UI (Zero memory leaks).
 *   ✓ Fire-and-forget resilient background saving.
 *   ✓ Fallback Adapter pattern for legacy UI context (`_ctx`).
 */

import { loadDICOMFiles } from '../loaders/loaderManager.js';
import { workersAvailable, parseFilesWithWorkers, замерПарсинга } from '../loaders/workerPool.js';
import { сводка as сводкаЭтапов, ПОКОЛЕНИЕ_ФОРМУЛИРОВОК } from '../loaders/loadStages.js';
import { registerWorkerResults, getLastRegisterStats } from '../loaders/loaderWorkerBridge.js';
import { clearAll } from '../core/storage.js';
import { showToast, escapeHtml, notifyFolderLoadCancelled, notifyDecimation } from '../utils/helpers.js';
import { getRecommendedBatchSize, askAboutBigSeries } from '../core/gpuDetector.js';
import { purgeCornerstoneCache } from '../core/viewport.js';
import { log } from '../utils/logger.js';
import { saveStudy, searchStudies, getStudies, deleteStudy, loadStudyFiles, getStorageEstimate, clearAll as clearAllIDB } from '../storage/dicomStore.js';
// [9.0.0] Отмечаем, какое исследование открыто: только по этой отметке после
// выгрузки вкладки можно понять, есть ли что предложить открыть заново.
import { noteStudyOpened } from '../core/sessionState.js';
// [10.19.1] Автопоказ после загрузки не должен трогать окна сравнения.
import { isCompareActive } from '../core/compareState.js';
import { attachPendingThinning, noteWholeSeries, thinningSnapshot } from '../core/loadThinning.js';
// [10.19.5] Плашка замера — только когда она о беде. Правило и сам замер — в
// core/loadReport.js, под тестом.
import { запомнитьЗамер, последнийЗамер, нужноПоказать, КЛЮЧ_ПЛАШКИ_ЗАМЕРА, плашкаЗамераВыключена, показыватьСУчётомВыключателя } from '../core/loadReport.js';
// [12.0.41] Видео УЗИ в AVI (MJPEG) — в многокадровый УЗИ-DICOM до всех проверок.
import { заменитьAVIнаDICOM } from '../io/aviImport.js';

// ══════════════════════════════════════════════════════════════════
// 1. CONFIGURATION & SELECTORS
// ══════════════════════════════════════════════════════════════════

const DOM = Object.freeze({
  BTN_FILE: 'btn-open-file',
  BTN_FOLDER: 'btn-open-folder',
  BTN_URL: 'btn-open-url',
  FILE_INPUT: 'file-input',
  FOLDER_INPUT: 'folder-input',
  BTN_WADO: 'btn-wado-load',
  ARCHIVE_BTN: 'btn-archive',
  ARCHIVE_CLEAR: 'btn-archive-clear',
  ARCHIVE_VERIFY: 'btn-archive-verify',   // [10.19.46] переезд из Enterprise
  ARCHIVE_EXPORT_ALL: 'btn-archive-export-all',
  ARCHIVE_SEARCH: 'archive-search',
  ARCHIVE_LIST: 'archive-list',
  ARCHIVE_INFO: 'archive-storage-info',
  SHARE_QR: 'btn-share-qr',
  ANON_BTN: 'btn-anonymize',
  ANON_RUN: 'btn-anon-run',
  ANON_EXPORT: 'btn-anon-export',
});

// ══════════════════════════════════════════════════════════════════
// 2. CORE ENGINE CLASS
// ══════════════════════════════════════════════════════════════════

class FileOpsEngine {
  constructor() {
    this._ctx = null;
    this._state = {
      lastLoadedFiles: [],
      useWorkerPath: false,
    };
    
    /** @type {AbortController | null} */
    this._abortController = null;
    
    /** @type {Array<{el: HTMLElement, ev: string, fn: EventListener}>} */
    this._listeners = [];

    this._handleArchiveListClick = this._handleArchiveListClick.bind(this);
  }

  // ─── INITIALIZATION ──────────────────────────────────────────────

  init(ctx) {
    if (this._ctx) return; // Prevent double initialization
    
    // Adapter for UI methods (Defensive programming)
    this._ctx = {
      renderSeriesList: ctx.renderSeriesList || (() => {}),
      loadSeriesIntoViewport: ctx.loadSeriesIntoViewport || (async () => {}),
      showLoadingOverlay: ctx.showLoadingOverlay || (() => {}),
      updateLoadingOverlay: ctx.updateLoadingOverlay || (() => {}),
      hideLoadingOverlay: ctx.hideLoadingOverlay || (() => {}),
      openModal: ctx.openModal || (() => {}),
      closeModal: ctx.closeModal || (() => {}),
      getAllSeries: ctx.getAllSeries || (() => []),
      getCurrentSeries: ctx.getCurrentSeries || (() => null),
    };

    this._bindAllEvents();
    log.debug('[FileOps] Engine initialized successfully');
  }

  destroy() {
    this._listeners.forEach(({ el, ev, fn }) => el.removeEventListener(ev, fn));
    this._listeners = [];
    this.cancelLoad();
    this._ctx = null;
  }

  // ─── STATE ACCESSORS ─────────────────────────────────────────────

  getLastLoadedFiles() { return this._state.lastLoadedFiles; }
  setLastLoadedFiles(files) { this._state.lastLoadedFiles = files; }
  isWorkerPath() { return this._state.useWorkerPath; }
  isLoadCancelled() { return this._abortController ? this._abortController.signal.aborted : false; }

  cancelLoad() {
    if (this._abortController) {
      this._abortController.abort();
      log.info('[FileOps] Load cancelled by user');
    }
  }

  // ─── MAIN LOADING PIPELINE ───────────────────────────────────────

  async handleFiles(filesArray) {
    if (!filesArray || !filesArray.length) return;
    // [10.5.1] Замер по этапам. Без чисел с настоящей машины любой разговор о
    // скорости — обмен мнениями. Метки уходят в журнал одной строкой.
    const _t0 = Date.now();
    // Браузер закончил собирать список — секундомер ожидания останавливаем и
    // запоминаем, сколько эта чужая работа заняла.
    let _обход = 0;
    try {
      clearInterval(window.__обходТаймер); window.__обходТаймер = null;
      if (window.__обходНачало) { _обход = Math.round((_t0 - window.__обходНачало) / 1000); window.__обходНачало = 0; }
    } catch (e) { /* */ }
    try { window.__этапы = { файлов: filesArray.length, начало: _t0, обход: _обход }; } catch (e) { /* */ }

    // [12.0.41] ВИДЕО AVI С УЗИ-АППАРАТА. Проверка подписи DICM ниже
    // выбрасывала его молча — владелец выгрузил с Mindray DC-40 эхо в AVI, и
    // программа его «не видела». Здесь каждый .avi становится многокадровым
    // УЗИ-DICOM (кадры JPEG переносятся без пережатия), дальше его везёт
    // обычный путь кино. Файлы без расширения .avi не читаются — см.
    // io/aviImport.js. Двери загрузки (папка, перетаскивание, поле файлов,
    // ZIP) сходятся сюда, поэтому дверь одна. В хранилище браузера ляжет
    // уже DICOM — из архива программы он откроется без повторной переделки.
    filesArray = await заменитьAVIнаDICOM(filesArray, (номер, всего, имя) =>
      this._ctx.showLoadingOverlay('Видео AVI → кадры УЗИ…', `${номер} / ${всего}: ${имя}`, 0));
    if (!filesArray.length) { this._ctx.hideLoadingOverlay(); return; }

    // Reset cancellation token
    this._abortController = new AbortController();
    const signal = this._abortController.signal;

    this._ctx.showLoadingOverlay('Подготовка...', `${filesArray.length} файлов`, 0);

    try {
      if (workersAvailable()) {
        const success = await this._processWorkerPath(filesArray, signal);
        if (success) return;
      }
    } catch (e) {
      if (e.name === 'AbortError') throw e;
      log.warn('[FileOps] Worker path failed, falling back to main thread:', e.message);
    }

    if (!signal.aborted) {
      await this._processMainThread(filesArray, signal);
    }
  }

  /** Записать в журнал, сколько заняли этапы. Зовётся из _finalizeLoading. */
  _скажиВремя(t0, файлов) {
    try {
      const всего = ((Date.now() - t0) / 1000).toFixed(1);
      const наФайл = файлов ? ((Date.now() - t0) / файлов).toFixed(1) : '?';
      const сохранение = (localStorage.getItem('mikhmel_bg_save') === 'off') ? 'ВЫКЛ' : 'вкл';
      const обход = (() => { try { return window.__этапы?.обход || 0; } catch (e) { return 0; } })();
      const итого = (обход ? ` Всего с момента выбора папки: ${(Number(всего) + обход).toFixed(1)} с.` : '');
      const строкаОбхода = обход ? `браузер собирал список файлов ${обход} с, затем ` : '';
      const текст = `${строкаОбхода}работа программы ${всего} с на ${файлов} файлов (${наФайл} мс на файл).${итого} Фоновое сохранение: ${сохранение}`;
      // [11.1.35] Выключатель плашек (Диагностика): при выключенном замере
      // строки скорости уходят в журнал уровнем info, а не WARN — владелец
      // просил скрыть, не удалить. Данные живы: кнопка «Замер последней
      // загрузки» показывает их по требованию.
      const замерСкрыт = (() => {
        try { return плашкаЗамераВыключена(localStorage.getItem(КЛЮЧ_ПЛАШКИ_ЗАМЕРА)); }
        catch (e) { return false; }
      })();
      const скажи = замерСкрыт ? log.info : log.warn;
      скажи('[Время] ' + текст);

      // [10.8.1] Сверка: сколько файлов приняли и сколько срезов получилось.
      //
      // Ради этой строки всё и затевалось. Две ошибки 10.8.0 выглядели
      // одинаково — файлов в папке больше, чем срезов в программе, — и увидеть
      // это можно было, только пересчитав файлы вручную. Считать должна
      // программа. Не сошлось — говорит вслух и красным.
      const сверка = (() => {
        try {
          const с = getLastRegisterStats();
          if (!с || !с.files || Date.now() - с.at > 120000) return null;
          const потеря = с.files - с.frames - с.skipped;
          const части = [`Файлов принято ${с.files}`, `срезов получилось ${с.frames}`];
          if (с.skipped) части.push(`служебных объектов без снимка ${с.skipped}`);
          if (потеря > 0) {
            части.push(`ПОТЕРЯНО ${потеря} — столько файлов не стали срезами`);
            return { текст: части.join(' · '), плохо: true };
          }
          // Кадров может быть БОЛЬШЕ файлов: многокадровые съёмки (кино) дают
          // несколько снимков из одного файла. Это не потеря, а норма.
          части.push(с.frames > с.files ? 'многокадровые файлы — срезов больше' : 'сходится');
          return { текст: части.join(' · '), плохо: false };
        } catch (e) { return null; }
      })();
      // Потеря срезов — не замер скорости, а предупреждение о неполных
      // данных: её строка остаётся WARN и при скрытом замере.
      if (сверка) (сверка.плохо ? log.warn : скажи)('[Сверка] ' + сверка.текст);
      // [10.5.2] Замер нужен НА ЭКРАНЕ: он делается на телефоне, а печатался в
      // консоль разработчика, которой на Android нет.
      //
      // [10.19.5] Но не после КАЖДОЙ загрузки. Запоминаем всегда, а на экран
      // выносим только когда замер о беде: файлы не сошлись со срезами или
      // подозрительно долго на файл (правило — loadReport.нужноПоказать).
      // В остальное время он доступен кнопкой в Enterprise → Диагностика.
      const мсНаФайл = файлов ? (Date.now() - t0) / файлов : 0;
      // [10.19.23] Этапы — в замер и в журнал ОТДЕЛЬНЫМИ строками. Одно число
      // «столько-то мс на файл» говорит, что стало медленно, но не говорит где;
      // строки этапов называют виновника сразу: обход, чтение, разбор,
      // регистрация. На телефоне консоли нет, поэтому они же идут в плашку.
      let этапы = null;
      try {
        const снимок = window.__этапыЗамер;
        if (снимок) {
          этапы = сводкаЭтапов(снимок);
          for (const с of этапы) скажи('[Этапы] ' + с);
        }
      } catch (e) { /* замер — не повод ломать загрузку */ }
      запомнитьЗамер({ текст, сверка, файлов, мсНаФайл, секунд: Number(всего), при: Date.now(), этапы });
      // [11.1.35] Решение о показе — через выключатель: правило в
      // loadReport.показыватьСУчётомВыключателя, там же честная оговорка
      // про потерю срезов (она показывается всегда). «этапы» в нужноПоказать
      // НАМЕРЕННО не передаются, как и раньше: в норме сводка даёт 3–4
      // строки, и ветка «длиннее двух» зажигала бы плашку после КАЖДОЙ
      // загрузки — ровно то, от чего владелец просил избавить.
      const реши = показыватьСУчётомВыключателя(нужноПоказать({ сверка, мсНаФайл }), сверка, замерСкрыт);
      log.info(`[Время] плашка на экране: ${реши.показать ? 'да' : 'нет'} — ${реши.причина}`);
      if (реши.показать) показатьЗамерЗагрузки();
      try { window.__этапыЗамер = null; } catch (e) { /* */ }
    } catch (e) { /* */ }
  }

  // ─── WORKER PATH ─────────────────────────────────────────────────

  async _processWorkerPath(files, signal) {
    this._ctx.updateLoadingOverlay(2, 'Парсинг в фоне (Workers)...', `0 / ${files.length}`);

    const { seriesMap, errors } = await parseFilesWithWorkers(
      Array.from(files),
      (parsed, total) => {
        if (signal.aborted) return;
        const pct = Math.round((parsed / total) * 80);
        this._ctx.updateLoadingOverlay(pct, 'Парсинг (Workers)...', `${parsed} / ${total}`);
      }
    );

    if (signal.aborted) { this._ctx.hideLoadingOverlay(); return true; }

    const seriesCount = Object.keys(seriesMap).length;
    if (seriesCount === 0) {
      this._ctx.hideLoadingOverlay();
      showToast(errors.length ? `❌ Не найдено DICOM (${errors.length} ошибок)` : '❌ No DICOM files found', 'error');
      return true;
    }

    if (errors.length > 3) showToast(`⚠️ Пропущено ${errors.length} не-DICOM`, 'warning', 2000);

    this._ctx.updateLoadingOverlay(82, 'Регистрация в Cornerstone...', `${seriesCount} серий`);
    // [10.19.23] Регистрация — отдельный этап замера. До сих пор «парсинг и
    // регистрация» считались одним числом, и по нему нельзя было понять, где
    // ушли минуты: в чтении файлов, в разборе заголовков или в заведении
    // снимков в Cornerstone. Теперь каждое своё.
    const _тРег = Date.now();
    const list = await registerWorkerResults(seriesMap);
    try {
      window.__этапыЗамер = { ...(замерПарсинга() || {}) };
      window.__этапыЗамер.мс = { ...(window.__этапыЗамер.мс || {}), регистрация: Date.now() - _тРег };
      const о = window.__этапы?.обход;
      if (о) window.__этапыЗамер.мс.обход = о * 1000;
    } catch (e) { /* замер — не повод ломать загрузку */ }

    if (!list.length || signal.aborted) {
      this._ctx.hideLoadingOverlay();
      if (!signal.aborted) showToast('❌ Совмещение не удалось', 'error');
      return null; // Fallback to main thread
    }

    this._finalizeLoading(list, files, seriesMap, true);
    return true;
  }

  // ─── MAIN THREAD PATH ────────────────────────────────────────────

  async _processMainThread(files, signal) {
    const validFiles = await this._validateDicomFiles(files, signal);
    if (signal.aborted) { this._ctx.hideLoadingOverlay(); return; }

    if (validFiles.length === 0) {
      this._ctx.hideLoadingOverlay();
      showToast('❌ Не найдено валидных DICOM файлов', 'error');
      return;
    }

    this._ctx.updateLoadingOverlay(10, `Парсинг DICOM...`, `0 / ${validFiles.length}`);

    const seriesMap = await loadDICOMFiles(validFiles, (loaded, total) => {
      if (signal.aborted) return;
      const pct = 10 + Math.round((loaded / total) * 80);
      this._ctx.updateLoadingOverlay(pct, `Парсинг DICOM...`, `${loaded} / ${total}`);
    });

    if (signal.aborted) { this._ctx.hideLoadingOverlay(); return; }

    const list = Object.values(seriesMap);
    if (!list.length) {
      this._ctx.hideLoadingOverlay();
      showToast('❌ Ошибка чтения DICOM', 'error');
      return;
    }

    this._finalizeLoading(list, validFiles, null, false);
  }

  // ─── VALIDATION ──────────────────────────────────────────────────

  /**
   * Безопасная проверка "Магических байтов" DICOM (DICM по смещению 128)
   */
  async _validateDicomFiles(files, signal) {
    const BATCH = getRecommendedBatchSize();
    const validFiles = [];
    let skippedCount = 0;

    this._ctx.showLoadingOverlay('Проверка файлов...', `${files.length} файлов`, 0);

    for (let i = 0; i < files.length; i += BATCH) {
      if (signal.aborted) return [];

      const batch = Array.from(files).slice(i, i + BATCH);
      const results = await Promise.all(batch.map(async (f) => {
        if (/\.(dcm|dicom)$/i.test(f.name)) return { f, valid: true };
        if (f.size < 132) return { f, valid: false };
        
        try {
          const buf = await f.slice(0, 132).arrayBuffer();
          // Защита от слишком коротких буферов (например, пустых файлов)
          if (buf.byteLength < 132) return { f, valid: false };
          
          const bytes = new Uint8Array(buf);
          // Standard DICOM Prefix "DICM"
          if (bytes[128] === 0x44 && bytes[129] === 0x49 && bytes[130] === 0x43 && bytes[131] === 0x4D) return { f, valid: true };
          // Part 10 missing preamble fallback
          if ((bytes[0] === 0x02 || bytes[0] === 0x08 || bytes[0] === 0x10) && bytes[1] === 0x00) return { f, valid: true };
          
          return { f, valid: false };
        } catch (e) {
          return { f, valid: true }; // При ошибке чтения пускаем дальше, парсер разберется
        }
      }));

      results.forEach(r => r.valid ? validFiles.push(r.f) : skippedCount++);
      
      const pct = Math.min(10, Math.round(((i + batch.length) / files.length) * 10));
      this._ctx.updateLoadingOverlay(pct, 'Проверка файлов...', `${i + batch.length} / ${files.length}`);
    }

    if (skippedCount > 3) showToast(`⚠️ Пропущено ${skippedCount} не-DICOM файлов`, 'warning', 2000);
    return validFiles;
  }

  // ─── FINALIZATION & BACKGROUND TASKS ─────────────────────────────

  _finalizeLoading(list, originalFiles, workerSeriesMap, isWorker) {
    this._state.useWorkerPath = isWorker;
    
    // Extract actual File objects for ZIP export feature
    const dicomFiles = [];
    if (isWorker && workerSeriesMap) {
      Object.values(workerSeriesMap).forEach(s => s.files.forEach(i => { if (i.file) dicomFiles.push(i.file); }));
    } else {
      dicomFiles.push(...originalFiles);
    }
    
    this._state.lastLoadedFiles = dicomFiles.length ? dicomFiles : Array.from(originalFiles);

    // Hide drop placeholder if exists
    const dp = document.getElementById('drop-placeholder');
    if (dp) dp.style.display = 'none';

    // [10.19.1] Серии разобраны — если загрузка шла с прореживанием, привязать
    // пометку именно к ним. Дальше её увидит всякий, кто считает по ним число.
    //
    // ВАЖНО: список `list` — накопленный, в нём и прежние исследования. Серии
    // ЭТОЙ загрузки на главном пути даёт workerSeriesMap; на запасном берём
    // список, а заведомо целые серии отфильтрует сама пометка.
    try {
      const свои = (isWorker && workerSeriesMap)
        ? Object.keys(workerSeriesMap)
        : (list || []).map((s) => s.seriesUID);
      if (thinningSnapshot().ждёт) attachPendingThinning(свои);
      else noteWholeSeries(свои);
    } catch (e) { /* */ }

    // Render & Load series
    this._ctx.renderSeriesList(list);
    // [6.4.0] По умолчанию открываем самую детальную серию — с наибольшим числом
    // срезов (обычно тонкосрезная 3D), а не первую по порядку (часто скаут/ранняя
    // T2). Локалайзеры и вторичные захваты исключаем. Врач может выбрать любую
    // другую серию вручную в панели серий.
    const _sliceCount = (s) => (s.images || s.files || []).length;
    const _looksService = (s) => /\b(loc|scout|localizer|report|screen|save|dose|key|secondary)\b/i.test(s.seriesDesc || '');
    const _diagnostic = list.filter(s => !_looksService(s));
    const _pool = _diagnostic.length ? _diagnostic : list;
    const _best = _pool.reduce((a, b) => (_sliceCount(b) > _sliceCount(a) ? b : a), _pool[0]);
    // [10.19.1] В режиме сравнения серии по окнам раскладывает сам раздел
    // сравнения (onStudiesChanged → ждущее окно). Автопоказ «самой детальной
    // серии» отсюда попадал в АКТИВНОЕ окно — то есть в чужое окно сравнения:
    // врач загружал второе исследование, а СТОПКА ПЕРВОГО ОКНА молча
    // подменялась новой серией, хотя подпись окна оставалась прежней.
    // Найдено зондом при закреплении 10.19.1 и воспроизведено на нетронутой
    // 10.19.0 (путь поля ввода). Вне сравнения поведение прежнее.
    if (!isCompareActive()) {
      this._ctx.loadSeriesIntoViewport((_best || list[0]).seriesUID).catch(e => log.error('Viewport load failed:', e));
    }
    this._ctx.hideLoadingOverlay();
    
    const totalImgs = list.reduce((s, ser) => s + (ser.images || ser.files || []).length, 0);
    showToast(`✅ ${list.length} серий, ${totalImgs} изображений (${isWorker ? 'Workers' : 'Main Thread'})`, 'success');

    try { this._скажиВремя(window.__этапы?.начало || Date.now(), window.__этапы?.файлов || 0); } catch (e) { /* */ }

    // Fire-and-forget save
    this._backgroundSaveStudy(list, this._state.lastLoadedFiles);
  }

  async _backgroundSaveStudy(list, files) {
    // [10.5.1] Выключатель фонового сохранения — для опыта на телефоне.
    //
    // Это место сохраняет ВСЁ исследование в хранилище браузера, чтобы объём
    // пережил выгрузку вкладки. Цена: каждый файл читается ещё раз целиком и
    // пишется в IndexedDB — на пяти тысячах срезов это гигабайты. И делается
    // это «в фоне», то есть ОДНОВРЕМЕННО со сборкой тома: та же вкладка, та
    // же память, та же очередь задач. На компьютере фон и есть фон, на
    // телефоне трое конкурируют за одно горло.
    //
    // Выключатель нужен, чтобы измерить, а не гадать: один запуск с ним и
    // один без него разделяют две причины — хранилище или память.
    try {
      if (localStorage.getItem('mikhmel_bg_save') === 'off') {
        log.warn('[Сохранение] фоновое сохранение исследования ВЫКЛЮЧЕНО (опыт). Объём не переживёт выгрузку вкладки.');
        return;
      }
    } catch (e) { /* */ }
    try {
      const first = list[0];
      const studyId = `study_${first.studyUID || first.studyInstanceUID || Date.now()}`;
      const totalImages = list.reduce((s, ser) => s + (ser.images || ser.files || []).length, 0);
      const validFiles = Array.from(files).filter(f => f.size > 132 && !f.name.startsWith('.'));
      
      try { noteStudyOpened({ studyId, patientName: first.patientName, label: first.studyDesc }); } catch (e) { /* */ }
      await saveStudy({
        id: studyId,
        patientName: first.patientName || 'Unknown',
        patientId: first.patientId || '',
        studyDate: first.studyDate || '',
        studyDesc: first.studyDesc || '',
        modality: [...new Set(list.map(s => s.modality))].join('/'),
        seriesCount: list.length,
        totalImages: totalImages,
      }, validFiles);
      
      log.debug(`[IDB] Background save complete: ${validFiles.length} files`);
    } catch (e) {
      log.warn('[IDB] Background save failed. Ensure IndexedDB limits are not exceeded.', e);
    }
  }

  // ─── ARCHIVE MANAGEMENT ──────────────────────────────────────────

  async _refreshArchiveList(searchQuery = '') {
    const listEl = document.getElementById(DOM.ARCHIVE_LIST);
    if (!listEl) return;
    
    listEl.innerHTML = '<div style="padding:20px; text-align:center; color:var(--text-dim);">Загрузка...</div>';

    try {
      const studies = (searchQuery && searchQuery.length >= 2) 
        ? await searchStudies(searchQuery) 
        : await getStudies();
        
      this._updateStorageEstimate();

      if (!studies.length) {
        listEl.innerHTML = '<div style="padding:30px; text-align:center; color:var(--text-dim);">Архив пуст. Загруженные DICOM сохраняются автоматически.</div>';
        return;
      }

      studies.sort((a, b) => (b.savedAt || '').localeCompare(a.savedAt || ''));
      
      // Использование DocumentFragment для оптимизации рендеринга
      const frag = document.createDocumentFragment();
      const modIcon = { CT: '🫁', MR: '🧠', US: '❤️', XR: '🦴', MG: '🩻', PT: '☢️' };

      studies.forEach(s => {
        const div = document.createElement('div');
        div.className = 'archive-item';
        div.dataset.studyId = s.id; // Для Event Delegation
        
        const icon = modIcon[s.modality?.split('/')[0]] || '📋';
        const date = s.studyDate ? this._formatStudyDate(s.studyDate) : (s.savedAt ? new Date(s.savedAt).toLocaleDateString() : '—');
        
        div.innerHTML = `
          <div class="archive-icon">${icon}</div>
          <div class="archive-info">
            <div class="archive-name">${escapeHtml(s.patientName) || 'Unknown'}</div>
            <div class="archive-meta">${escapeHtml(s.modality) || '—'} • ${s.seriesCount || 0} серий • ${s.totalImages || 0} кадров • ${escapeHtml(date)}</div>
            ${s.studyDesc ? `<div class="archive-meta">${escapeHtml(s.studyDesc)}</div>` : ''}
          </div>
          <div class="archive-size">${this._formatBytes(s.sizeBytes || 0)}</div>
          <button class="archive-del" data-action="delete" title="Удалить">✕</button>
        `;
        frag.appendChild(div);
      });

      listEl.innerHTML = '';
      listEl.appendChild(frag);

    } catch (err) {
      listEl.innerHTML = `<div style="padding:20px; text-align:center; color:var(--danger);">Ошибка: ${escapeHtml(err.message)}</div>`;
    }
  }

  /**
   * Event Delegation: Обрабатываем клики по всему списку одним Listener'ом
   */
  async _handleArchiveListClick(e) {
    const item = e.target.closest('.archive-item');
    if (!item) return;

    const studyId = item.dataset.studyId;
    const isDelete = e.target.closest('.archive-del');

    if (isDelete) {
      e.stopPropagation();
      if (!confirm('Удалить исследование из базы?')) return;
      
      await deleteStudy(studyId);
      purgeCornerstoneCache();
      item.remove();
      showToast('Удалено', 'info', 1000);
      
      this._updateStorageEstimate();
      const listEl = document.getElementById(DOM.ARCHIVE_LIST);
      if (!listEl.querySelector('.archive-item')) {
        listEl.innerHTML = '<div style="padding:30px; text-align:center; color:var(--text-dim);">Архив пуст</div>';
      }
      return;
    }

    // Load Study
    this._ctx.closeModal('modal-archive');
    this._ctx.showLoadingOverlay('Чтение из архива...', 'IDB Storage', 0);
    
    try {
      try { noteStudyOpened({ studyId }); } catch (e) { /* */ }
      const files = await loadStudyFiles(studyId, (loaded, total) => {
        this._ctx.updateLoadingOverlay(Math.round((loaded / total) * 80), 'Чтение БД...', `${loaded}/${total} файлов`);
      });
      
      if (files.length) {
        await this.handleFiles(files); // Re-use main pipeline
      }
    } catch (err) {
      this._ctx.hideLoadingOverlay();
      showToast(`❌ Ошибка чтения: ${err.message}`, 'error');
    }
  }

  async _updateStorageEstimate() {
    try {
      const est = await getStorageEstimate();
      const info = document.getElementById(DOM.ARCHIVE_INFO);
      if (info) info.textContent = `${this._formatBytes(est.used)} / ${this._formatBytes(est.quota)}`;
    } catch (e) { log.debug("[fileOps]", e.message); }
  }

  // ─── SHARING & ANONYMIZATION ─────────────────────────────────────

  async _generateShare() {
    try {
      const seriesUID = this._ctx.getCurrentSeries();
      const series = this._ctx.getAllSeries().find(s => s.seriesUID === seriesUID) || {};
      
      // Попытка отправить скриншот через Web Share API
      if (navigator.share) {
        const canvas = document.querySelector('#viewport-container canvas');
        if (canvas) {
          canvas.toBlob(async (blob) => {
            if (!blob) return;
            const file = new File([blob], 'dicom_screenshot.png', { type: 'image/png' });
            try {
              await navigator.share({
                title: `DICOM: ${series.patientName || 'Study'}`,
                text: `${series.modality || ''} — ${series.seriesDesc || 'Series'}`,
                files: [file]
              });
              showToast('✅ Отправлено', 'success', 1000);
              return;
            } catch (e) {
              if (e.name !== 'AbortError') throw e; // Pass to fallback
            }
          }, 'image/png');
          return;
        }
      }
      
      // Fallback: Копирование URL
      await navigator.clipboard.writeText(window.location.href);
      showToast('📋 Ссылка скопирована в буфер', 'success', 2000);
      
    } catch (err) {
      showToast(`❌ Ошибка API «Поделиться»: ${err.message}`, 'error');
    }
  }

  _runAnonymization() {
    // [10.19.32] Гейт «выберите хотя бы один тег» УДАЛЁН вместе с чтением
    // переключателей: сами переключатели были иллюзией выбора — этот же метод
    // ниже честно признавал «галочки на выбор полей больше не влияют», но
    // разметку с семью выключателями и полем «Новый ID» (тоже мёртвым —
    // void newId) не привёл в соответствие. Аудит 10.19.32 снял вывески из
    // окна; первый заход при этом СЛОМАЛ запуск (пустой список → вечный
    // отказ) — ошибка аудитора, вскрытая вопросом владельца «есть ошибки?».
    // Теперь у окна нет ложных ручек, а у запуска — ложного гейта.
    const status = document.getElementById('anon-status');

    // [10.19.9] ЗДЕСЬ БЫЛО ПЛАЦЕБО: setTimeout на 500 мс и «✅ Анонимизация
    // завершена», не изменив ни байта. В коде так и было написано — «Имитация
    // работы для UI». Для медицинской программы это худший класс дефекта: врач
    // получал подтверждение операции, которой не было, и мог отдать файлы
    // наружу. В 10.19.9 убран ложный рапорт, в 10.19.10 сделана сама работа.
    //
    // Что происходит теперь: спрашиваем файлы, обезличиваем КОПИЮ и отдаём
    // ZIP. Исходники не трогаем намеренно — ошибка в обезличивании тогда не
    // портит исследование. Галочки тегов в окне на выбор полей больше не
    // влияют: профиль применяется целиком (иначе «обезличено частично» — это
    // снова обещание без исполнения).
    // [10.19.32] Разметка приведена к этой же правде: семь переключателей и
    // поле «Новый ID» убраны из окна как иллюзия (см. index.html).
    if (status) {
      status.innerHTML = '🔄 Выберите файлы DICOM — обезличенная <b>копия</b> уйдёт отдельным ZIP. '
        + 'Исходные файлы не изменяются.';
      status.style.color = 'var(--accent)';
    }
    import('../io/deidentifyRun.js').then(async (m) => {
      const итог = await m.обезличитьВыбранные({
        сообщить: (т) => { if (status) { status.textContent = т; status.style.color = 'var(--accent)'; } },
      });
      if (status) {
        if (итог) {
          status.innerHTML = `✅ Обезличено файлов: <b>${итог.файлов}</b>. Архив сохранён. `
            + 'Исходные файлы не изменены. Надписи, вжжённые в изображение, заголовком не убираются.';
          status.style.color = 'var(--success)';
        } else {
          status.textContent = 'Обезличивание не выполнено — файлы не выбраны или это не DICOM.';
          status.style.color = '#eab308';
        }
      }
    }).catch((e) => {
      if (status) { status.textContent = 'Обезличивание недоступно: ' + e.message; status.style.color = '#f87171'; }
    });
  }

  // ─── EVENT BINDING UTILS ─────────────────────────────────────────

  _bind(id, evt, fn) {
    const el = document.getElementById(id);
    if (el) {
      const boundFn = fn.bind(this);
      el.addEventListener(evt, boundFn);
      this._listeners.push({ el, ev: evt, fn: boundFn });
    }
  }

  _bindAllEvents() {
    // 1. File Inputs
    this._bind(DOM.BTN_FILE, 'click', () => document.getElementById(DOM.FILE_INPUT)?.click());
    // [10.5.5] Кнопка «Папка» идёт через openFolderPicker, а не в скрытое поле.
    //
    // Здесь стояло прямое нажатие на <input webkitdirectory>. Из-за этого всё,
    // что лежит в openFolderPicker, по этой кнопке не выполнялось вовсе — в
    // том числе окно «Читаю список файлов…» с бегущими секундами. Врач нажимал
    // «Папку», выбирал каталог и три минуты смотрел на неподвижный экран: окно
    // существовало в коде, но путь к нему шёл мимо.
    //
    // В appInit.js эту же кнопку однажды уже чинили тем же способом (10.1.9), и
    // там об этом написан комментарий. А здесь, в единственном живом месте
    // привязки, осталось прямое нажатие — и перекрыло собой ту починку.
    this._bind(DOM.BTN_FOLDER, 'click', () => {
      try { openFolderPicker(); }
      catch (e) { document.getElementById(DOM.FOLDER_INPUT)?.click(); }
    });
    this._bind(DOM.BTN_URL, 'click', () => this._ctx.openModal('modal-wado'));
    
    this._bind(DOM.FILE_INPUT, 'change', (e) => this.handleFiles(Array.from(e.target.files)));
    this._bind(DOM.FOLDER_INPUT, 'change', (e) => {
      const files = Array.from(e.target.files).filter(f => !f.name.startsWith('.') && f.size > 128);
      this.handleFiles(files);
    });

    this._bind(DOM.BTN_WADO, 'click', () => {
      const url = document.getElementById('wado-url')?.value;
      if (url) { showToast('🌐 Загрузка по WADO...', 'info'); this._ctx.closeModal('modal-wado'); }
    });

    // 2. Archive
    this._bind(DOM.ARCHIVE_BTN, 'click', async () => {
      this._ctx.openModal('modal-archive');
      await this._refreshArchiveList();
    });
    this._bind(DOM.ARCHIVE_CLEAR, 'click', async () => {
    // [10.19.46] Обе кнопки переехали из панели по рекомендации, принятой
    // владельцем: они единственные в той секции не имели дома.
    this._bind(DOM.ARCHIVE_VERIFY, 'click', async () => {
      // Живое имя ядра — verifyAllStudies (возвращает МАССИВ битых, не сводку).
      // Проверено по dicomStore, а не по памяти: выдуманное имя молча
      // проглотил бы catch, и кнопка врала бы «проверка не удалась».
      const { verifyAllStudies } = await import('../storage/dicomStore.js');
      showToast('Проверяю целостность архива…', 'info', 1500);
      try {
        const битые = await verifyAllStudies();
        showToast(битые.length
          ? `Повреждённых исследований: ${битые.length} — ${битые.slice(0, 3).map((b) => b.patientName || b.id).join(', ')}`
          : 'Целостность в порядке: потерянных файлов не найдено',
          битые.length ? 'warning' : 'success', 5000);
      } catch (e) { showToast('Проверка не удалась: ' + e.message, 'error', 3000); }
    });
    this._bind(DOM.ARCHIVE_EXPORT_ALL, 'click', async () => {
      const { exportAllStudies } = await import('../storage/dicomStore.js');
      showToast('Выгружаю архив целиком…', 'info', 2000);
      try {
        const файлы = await exportAllStudies((c, t) => showToast(`Выгружено ${c}/${t}`, 'info', 800));
        showToast(файлы?.length ? `Выгружено исследований: ${файлы.length}` : 'В архиве пусто', файлы?.length ? 'success' : 'info', 4000);
      } catch (e) { showToast('Выгрузка не удалась: ' + e.message, 'error', 3000); }
    });
      if (!confirm('Удалить все сохранённые исследования?')) return;
      await clearAllIDB();
      purgeCornerstoneCache();
      await this._refreshArchiveList();
      showToast('🗑️ Архив очищен', 'info');
    });
    this._bind(DOM.ARCHIVE_SEARCH, 'input', (e) => this._refreshArchiveList(e.target.value.trim()));
    
    // Memory-safe Event Delegation for the archive list
    this._bind(DOM.ARCHIVE_LIST, 'click', this._handleArchiveListClick);

    // 3. Share & Anon
    this._bind(DOM.SHARE_QR, 'click', this._generateShare);
    this._bind(DOM.ANON_BTN, 'click', () => this._ctx.openModal('modal-anonymize'));
    this._bind(DOM.ANON_RUN, 'click', this._runAnonymization);
    
    // Anonymize toggles (Event delegation)
    const anonModal = document.getElementById('modal-anonymize');
    if (anonModal) {
      const toggleHandler = (e) => {
        if (e.target.classList.contains('ctrl-toggle')) e.target.classList.toggle('on');
      };
      anonModal.addEventListener('click', toggleHandler);
      this._listeners.push({ el: anonModal, ev: 'click', fn: toggleHandler });
    }
  }

  // ─── FORMATTERS ──────────────────────────────────────────────────

  _formatBytes(bytes) {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    if (bytes < 1073741824) return (bytes / 1048576).toFixed(1) + ' MB';
    return (bytes / 1073741824).toFixed(2) + ' GB';
  }

  _formatStudyDate(d) {
    if (!d || d.length < 8) return d || '—';
    return `${d.substring(6,8)}.${d.substring(4,6)}.${d.substring(0,4)}`;
  }
}

// ══════════════════════════════════════════════════════════════════
// 3. EXPORTS & COMPATIBILITY LAYER
// ══════════════════════════════════════════════════════════════════

const fileOpsEngine = new FileOpsEngine();

export const initFileOps = (ctx) => fileOpsEngine.init(ctx);
export const handleFiles = (files) => fileOpsEngine.handleFiles(files);
export const cancelLoad = () => fileOpsEngine.cancelLoad();

// Getters/Setters
export const getLastLoadedFiles = () => fileOpsEngine.getLastLoadedFiles();
export const setLastLoadedFiles = (f) => fileOpsEngine.setLastLoadedFiles(f);
export const isWorkerPath = () => fileOpsEngine.isWorkerPath();
export const setUseWorkerPath = (v) => fileOpsEngine._state.useWorkerPath = v;
export const isLoadCancelled = () => fileOpsEngine.isLoadCancelled();

export const destroyFileOps = () => fileOpsEngine.destroy();
// ══════════════════════════════════════════════════════════════
// ENTERPRISE FEATURES (v6) — Real functionality
// ══════════════════════════════════════════════════════════════

/** @type {Array<{timestamp:number, fileCount:number, totalSizeMB:number, duration:number}>} */
const _loadHistory = [];
const LOAD_HISTORY_MAX = 30;

/**
 * Get file load history with performance metrics
 * @returns {Array}
 */
export function getLoadHistory() { return [..._loadHistory]; }

/**
 * Analyze files before loading — detect types, estimate size, check for DICOM
 * @param {FileList|File[]} files
 * @returns {{total:number, dicom:number, zip:number, other:number, totalSizeMB:number, extensions:Object}}
 */
export function analyzeFiles(files) {
  const stats = { total: 0, dicom: 0, zip: 0, other: 0, totalSizeMB: 0, extensions: {} };
  for (const f of files) {
    stats.total++;
    stats.totalSizeMB += f.size / 1048576;
    const ext = (f.name.split('.').pop() || '').toLowerCase();
    stats.extensions[ext] = (stats.extensions[ext] || 0) + 1;
    if (ext === 'dcm' || ext === 'ima' || !ext || f.name === 'DICOMDIR') stats.dicom++;
    else if (ext === 'zip') stats.zip++;
    else stats.other++;
  }
  stats.totalSizeMB = +stats.totalSizeMB.toFixed(1);
  return stats;
}

/**
 * Load files from URL array (e.g. WADO-RS links)
 * @param {string[]} urls
 */
export async function loadFromURLs(urls) {
  if (!urls?.length) return;
  const files = [];
  for (const url of urls) {
    try {
      const resp = await fetch(url);
      if (!resp.ok) continue;
      const blob = await resp.blob();
      const name = url.split('/').pop() || `remote_${Date.now()}.dcm`;
      files.push(new File([blob], name, { type: blob.type || 'application/dicom' }));
    } catch (e) { log.warn(`[FileOps] URL fetch failed: ${url}`, e.message); }
  }
  if (files.length) handleFiles(files);
  return files.length;
}

/**
 * Get estimated load time based on file count and GPU tier
 * @param {number} fileCount
 * @returns {{estimatedSec:number, recommendation:string}}
 */
export function estimateLoadTime(fileCount) {
  // Heuristic: ~50ms per file on mid-tier GPU, ~20ms on high-tier
  const perFile = window.MikhmelViewer?.getGPUInfo?.()?.tier?.label === 'High' ? 20 : 50;
  const sec = (fileCount * perFile) / 1000;
  let rec = 'Быстрая загрузка';
  if (sec > 30) rec = 'Рекомендуется шаг среза 2-3';
  if (sec > 60) rec = 'Рекомендуется шаг среза 3-5 или потоковая загрузка';
  return { estimatedSec: +sec.toFixed(1), recommendation: rec };
}

/**
 * Open native file picker with DICOM filter
 * @returns {Promise<FileList|null>}
 */
export async function openFilePicker() {
  if (window.showOpenFilePicker) {
    try {
      const handles = await window.showOpenFilePicker({
        types: [
          { description: 'DICOM Files', accept: { 'application/dicom': ['.dcm', '.ima'] } },
          { description: 'All Files', accept: { '*/*': [] } },
        ],
        multiple: true,
      });
      const files = await Promise.all(handles.map(h => h.getFile()));
      if (files.length) handleFiles(files);
      return files;
    } catch (e) { if (e.name !== 'AbortError') log.warn('[FileOps]', e.message); }
  } else {
    document.getElementById('file-input')?.click();
  }
  return null;
}

/**
 * Open native folder picker
 * @returns {Promise<File[]|null>}
 */
export async function openFolderPicker() {
  // [10.19.35] ТЕЛЕФОН ИДЁТ ПУТЁМ <input webkitdirectory> ПРИНУДИТЕЛЬНО.
  // Замер, ради которого строился секундомер обхода ниже («на телефоне — пять
  // минут; замер должен показать, где именно эта разница сидит»), владелец
  // снял на живых 620 файлах — и разница нашлась вся:
  //   путь showDirectoryPicker: обход 38.9 с (79% — getFile по одному),
  //     чтение 1425.9 мс/файл, итог 110 с работы;
  //   путь input (как в 10.19.32): обход внутри браузера, чтение
  //     163.3 мс/файл, итог ~13 с. Восьмикратная разница на одном телефоне.
  // На Android каждый FileSystemFileHandle платит межпроцессную цену ДВАЖДЫ:
  // при getFile на обходе и при каждом чтении через handle; File из input
  // приходит уже материализованным. Walker остаётся настольному пути — там
  // файловая система дешёвая, а живой счётчик найденных файлов ценен.
  // Определитель — по образцу IS_TOUCH из mobileStability.
  const телефонныйПуть = (() => {
    try {
      return navigator.maxTouchPoints > 0
        || window.matchMedia('(pointer: coarse)').matches
        || 'ontouchstart' in window;
    } catch (e) { return false; }
  })();
  if (!телефонныйПуть && window.showDirectoryPicker) {
    try {
      const dirHandle = await window.showDirectoryPicker();

      // [10.1.8] Показываем, что идёт обход папки, — сразу, а не потом.
      //
      // Раньше здесь молча собирались File-объекты по всему дереву, и только
      // после обхода появлялось окно «Подготовка…». На исследовании в тысячу
      // файлов это полминуты полной тишины: программа выглядит зависшей, и
      // человек тянется закрывать вкладку. Работы столько же — но теперь
      // видно, что она идёт.
      const say = (n) => {
        try {
          const api = window.MikhmelViewer;
          if (n === 0) api?.showLoadingOverlay?.('Просмотр папки…', 'Считаю файлы', 0);
          else api?.updateLoadingOverlay?.(0, 'Просмотр папки…', `Найдено файлов: ${n}`);
        } catch (e) { /* окна ещё нет — не беда */ }
      };

      // [10.5.10] ЗАМЕР ВНУТРИ ОБХОДА. Поведение не меняется ни на йоту:
      // считаем секундомером, сколько из общего времени обхода уходит именно
      // на getFile() — получение объекта файла у системы хранения, — а сколько
      // на всё остальное (перебор записей каталога). На ноутбуке обход идёт
      // меньше секунды, на телефоне — пять минут; замер должен показать, где
      // именно эта разница сидит. Числа выводятся на экран в конце обхода.
      const files = [];
      let _вызовов = 0;      // сколько раз позвали getFile
      let _мсGetFile = 0;    // сколько миллисекунд суммарно в нём провели
      let _мсМакс = 0;       // самый долгий одиночный вызов
      const _обходT0 = Date.now();
      async function* walk(handle) {
        for await (const entry of handle.values()) {
          if (entry.kind === 'file') {
            const t = Date.now();
            const f = await entry.getFile();
            const д = Date.now() - t;
            _вызовов++; _мсGetFile += д; if (д > _мсМакс) _мсМакс = д;
            yield f;
          }
          else if (entry.kind === 'directory') yield* walk(entry);
        }
      }
      say(0);
      let sinceReport = 0;
      for await (const file of walk(dirHandle)) {
        files.push(file);
        // Обновляем не на каждом файле: на тысяче файлов это тысяча лишних
        // перерисовок, и они сами по себе тормозят обход.
        // Раз в двести файлов: и счётчик обновить, и дать экрану перерисоваться.
        // Реже, чем в 10.1.8 (было 25) — там уступка потоку на каждом
        // двадцать пятом файле сама подтормаживала обход.
        if (++sinceReport >= 200) {
          sinceReport = 0;
          say(files.length);
          await new Promise(r => requestAnimationFrame(() => r()));
        }
      }
      say(files.length);
      // Показать замер на экране: без него число видно только в консоли,
      // которой на телефоне нет.
      try {
        const всего = ((Date.now() - _обходT0) / 1000).toFixed(1);
        const вGetFile = (_мсGetFile / 1000).toFixed(1);
        const наВызов = _вызовов ? (_мсGetFile / _вызовов).toFixed(1) : '0';
        const доля = (Date.now() - _обходT0) > 0
          ? Math.round(_мсGetFile / (Date.now() - _обходT0) * 100) : 0;
        const текст = `Обход каталога: ${всего} с на ${files.length} файлов. `
          + `Из них в получении файлов (getFile): ${вGetFile} с — это ${доля}% всего обхода, `
          + `${наВызов} мс на файл, самый долгий ${_мсМакс} мс. Остальное — перебор каталога.`;
        // [11.1.35] Выключатель плашек (Диагностика): владелец просил скрыть
        // панель о скорости — обе, эта и «время загрузки». Скрыть, не удалить:
        // замер считается и пишется в журнал (уровнем info, чтобы не спамить
        // WARN), просто не лезет на экран.
        const обходСкрыт = (() => {
          try { return плашкаЗамераВыключена(localStorage.getItem(КЛЮЧ_ПЛАШКИ_ЗАМЕРА)); }
          catch (e2) { return false; }
        })();
        (обходСкрыт ? log.info : log.warn)('[Замер обхода] ' + текст);
        document.getElementById('замер-обхода')?.remove();
        if (!обходСкрыт) {
        const w = document.createElement('div');
        w.id = 'замер-обхода';
        w.style.cssText = 'position:fixed;left:8px;right:8px;top:8px;z-index:40001;background:#12161f;border:1px solid rgba(150,180,225,0.5);border-radius:12px;padding:14px;color:#dbe6f5;font-size:13px;line-height:1.45;box-shadow:0 10px 40px rgba(0,0,0,0.7);';
        const t = document.createElement('div');
        t.style.cssText = 'user-select:text;-webkit-user-select:text;margin-bottom:10px;';
        t.textContent = текст;
        const row = document.createElement('div'); row.style.cssText = 'display:flex;gap:8px;';
        const c = document.createElement('button'); c.textContent = 'Скопировать';
        c.style.cssText = 'flex:1;padding:10px;border-radius:8px;border:1px solid rgba(150,180,225,0.5);background:rgba(30,42,64,0.95);color:#e6eefb;font-size:13px;';
        c.addEventListener('click', () => { try { navigator.clipboard.writeText(текст); c.textContent = 'Скопировано'; } catch (e) { c.textContent = 'Выделите вручную'; } });
        const o = document.createElement('button'); o.textContent = 'Закрыть'; o.style.cssText = c.style.cssText;
        o.addEventListener('click', () => w.remove());
        row.appendChild(c); row.appendChild(o);
        w.appendChild(t); w.appendChild(row);
        document.body.appendChild(w);
        }   // [11.1.35] конец if (!обходСкрыт) — плашка строится лишь при включённом замере
      } catch (e) { /* замер не должен мешать работе */ }
      // [10.5.9] Обход папки тоже спрашивает про прореживание.
      //
      // Здесь стояло прямое `handleFiles(files)`, и из-за этого страж-
      // прореживатель на этом пути не показывался вовсе: он висит на событии
      // от поля ввода, а этот путь идёт мимо поля. Снаружи выглядело так,
      // будто прореживание удалили. Никто его не удалял — просто мимо него
      // проложили дорогу. Теперь вопрос задаётся здесь же, тем же кодом, что
      // и на втором пути.
      if (files.length) {
        const выбор = await askAboutBigSeries(files);
        if (!выбор || !выбор.length) {
          // [10.19.0] МОЛЧАНИЕ ЗДЕСЬ — И БЫЛА ТА САМАЯ БЕДА.
          //
          // Врач выбрал каталог для второго окна сравнения, каталог обошёлся
          // (в отчёте «1071 файл»), всплыл вопрос о прореживании — и на отказ
          // (кнопка «Отмена» или щелчок мимо окна) программа не делала ничего
          // и НЕ ГОВОРИЛА НИЧЕГО. Снаружи: «просто не проявляется». Окно
          // сравнения так и оставалось с карточкой выбора, и понять, что
          // произошло, было нельзя ниоткуда.
          //
          // Отказ — законный исход, но о нём надо сказать вслух и вернуть
          // окно сравнения в понятное состояние.
          // [10.19.1] Реакция вынесена в notifyFolderLoadCancelled: тот же
          // отказ случается и на пути поля ввода, и молчать он не должен нигде.
          notifyFolderLoadCancelled();
          return null;
        }
        if (выбор.length < files.length) {
          // Прореживание идёт по всему каталогу разом, а не внутри серий:
          // на этом шаге серий ещё нет, есть только список файлов. Значит
          // ПОСТРАДАЮТ ВСЕ серии сразу, и объём очага с картой разницы,
          // посчитанные потом, окажутся занижены. Врач обязан знать об этом
          // сейчас, а не гадать над числом через месяц.
          notifyDecimation(выбор.length, files.length);
        }
        handleFiles(выбор);
      } else {
        try { window.MikhmelViewer?.hideLoadingOverlay?.(); } catch (e) { /* */ }
      }
      return files;
    } catch (e) {
      try { window.MikhmelViewer?.hideLoadingOverlay?.(); } catch (e2) { /* */ }
      // [10.19.1] Отмена самого системного диалога — тоже отказ: окно
      // сравнения не должно вечно числиться ждущим, иначе СЛЕДУЮЩАЯ любая
      // загрузка молча уедет в него. Тост не нужен — человек сам только что
      // нажал «Отмена», а карточка окна объяснит, что выбор отменён.
      try { window.MikhmelViewer?.compareOpenCancelled?.(); } catch (e2) { /* */ }
      if (e.name !== 'AbortError') log.warn('[FileOps]', e.message);
    }
  } else {
    // [10.2.0] Телефон: showDirectoryPicker Android-браузеры не умеют, и
    // список файлов собирает САМ БРАУЗЕР внутри <input webkitdirectory>.
    // Пока он это делает, никакой наш код не выполняется — событие change
    // наступит только в конце. Инструментировать это нечем: чужая работа.
    //
    // Но объяснить молчание можно. Окно показываем ДО открытия выбора: пока
    // открыт системный диалог, оно скрыто за ним, а как только человек
    // выбрал папку — оно на экране и честно говорит, что происходит и что
    // это надолго. Дальше handleFiles заменит его своим счётчиком.
    // [10.5.4] Окно с БЕГУЩИМИ СЕКУНДАМИ.
    //
    // Оно и раньше показывалось, но текст был неподвижен. На 5000 файлов
    // браузер собирает список около трёх минут, и неподвижная надпись всё это
    // время читается как зависание — врач не знает, ждать ему или закрывать.
    // Теперь на экране тикают секунды: видно, что программа жива.
    //
    // Ускорить это нельзя: в эти минуты не выполняется ни одной нашей строки,
    // список собирает сам браузер внутри <input webkitdirectory>. Но врать
    // молчанием тоже нельзя. Заодно засекаем, сколько заняла эта чужая работа,
    // — раньше мы её не мерили вовсе, и она не попадала ни в какие числа.
    try { window.__обходНачало = Date.now(); } catch (e) { /* */ }
    try {
      clearInterval(window.__обходТаймер);
      window.__обходТаймер = setInterval(() => {
        try {
          const сек = Math.round((Date.now() - (window.__обходНачало || Date.now())) / 1000);
          window.MikhmelViewer?.updateLoadingOverlay?.(0, 'Читаю список файлов…',
            `Прошло ${сек} с. Это делает браузер — программа ещё не начала работу. На большом исследовании занимает несколько минут.`);
        } catch (e) { /* */ }
      }, 1000);
    } catch (e) { /* */ }
    try {
      window.MikhmelViewer?.showLoadingOverlay?.(
        'Читаю список файлов…',
        'Это делает браузер, и на большом исследовании может занять несколько минут. Не закрывайте страницу. Совет: ZIP-архив тех же файлов открывается в десятки раз быстрее («Файл → Импорт ZIP»).',
        0,
      );
    } catch (e) { /* */ }
    const inp = document.getElementById('folder-input');
    // [10.5.6] Окно больше НЕ гасится по возвращении фокуса.
    //
    // Здесь стояло: вернулся фокус → через 0,8 с смотрим, есть ли файлы в
    // поле; если нет — считаем, что выбор отменили, и убираем окно. На
    // маленькой папке это верно. На пяти тысячах файлов — нет: браузер в этот
    // момент ещё только собирает список, поле пустое, и я гасил собственное
    // окно ожидания. Дальше человек три минуты смотрел в пустой экран — ровно
    // то, на что Вы и жаловались. Окно существовало, путь к нему был, и я же
    // его и выключал через восемьсот миллисекунд.
    //
    // Правильный признак конца ожидания только один — событие change, то есть
    // handleFiles: он и заменит это окно своим счётчиком. А если человек
    // действительно передумал, у окна загрузки есть кнопка «Отмена» — пусть
    // решает он, а не мой таймер.
    inp?.click();
  }
  return null;
}

// Patch handleFiles to track history
const _origHandleFiles = fileOpsEngine.handleFiles.bind(fileOpsEngine);
fileOpsEngine.handleFiles = async function(files) {
  const start = Date.now();
  // [10.5.7] ЗДЕСЬ БЫЛА ТА САМАЯ ТИШИНА.
  //
  // Стояла строка:
  //     const totalSize = Array.from(files).reduce((sum, f) => sum + f.size, 0);
  // Ради ОДНОЙ строчки в истории загрузок — «сколько мегабайт» — она опрашивала
  // размер каждого файла. На 4987 файлах это 4987 обращений подряд, синхронно,
  // в главном потоке, ДО начала настоящей загрузки.
  //
  // На компьютере файл лежит в обычной файловой системе, и размер известен
  // сразу — микросекунды, никто не замечал. На Android каждый File получен от
  // системного поставщика хранилища, и .size у него стоит отдельного обращения
  // между процессами. Пока цикл крутится, поток занят целиком: браузер не может
  // ни перерисовать экран, ни показать окно ожидания, ни обновить счётчик.
  // Снаружи это выглядит как зависшая программа — те самые три минуты, в
  // которые «ничего не происходит».
  //
  // Причём считалось это ДО загрузки, хотя нужно только ПОСЛЕ, для справки. И
  // считать надо не спеша: размер берём порциями, между ними отдаём поток
  // экрану. Если браузер не даст времени — обойдёмся без цифры, история
  // загрузок не стоит трёх минут врачебного времени.
  await _origHandleFiles(files);
  const запись = {
    timestamp: Date.now(), fileCount: files.length,
    totalSizeMB: null, duration: Date.now() - start,
  };
  _loadHistory.push(запись);
  if (_loadHistory.length > LOAD_HISTORY_MAX) _loadHistory.shift();
  _посчитайРазмерНеспеша(files, запись);
};

/**
 * Сложить размеры файлов порциями, не занимая поток.
 * Порция небольшая, между порциями — уступка экрану. Результат дописывается в
 * уже сохранённую запись истории: она никого не ждёт.
 */
function _посчитайРазмерНеспеша(files, запись) {
  try {
    const список = Array.from(files);
    let i = 0, сумма = 0;
    const ПОРЦИЯ = 100;
    const шаг = () => {
      const конец = Math.min(i + ПОРЦИЯ, список.length);
      for (; i < конец; i++) { try { сумма += список[i]?.size || 0; } catch (e) { /* */ } }
      if (i < список.length) {
        (window.requestIdleCallback || window.requestAnimationFrame || setTimeout)(шаг, 0);
      } else {
        запись.totalSizeMB = +(сумма / 1048576).toFixed(1);
      }
    };
    (window.requestIdleCallback || window.requestAnimationFrame || setTimeout)(шаг, 0);
  } catch (e) { /* без цифры проживём */ }
}


/**
 * [10.19.5] Плашка с последним замером загрузки — на экран, по требованию.
 *
 * Отдельной функцией, потому что зовут её теперь двое: сама загрузка — когда
 * замер о беде (файлы не сошлись со срезами или слишком долго на файл), и
 * кнопка в Enterprise → Диагностика — когда врач захотел посмотреть. Прежде
 * это была ветка внутри _скажиВремя, и увидеть замер задним числом было
 * нельзя: только повторить загрузку целиком.
 *
 * Окно висит, пока его не закроют; текст можно выделить и скопировать кнопкой.
 */
export function показатьЗамерЗагрузки() {
  const з = последнийЗамер();
  if (!з) {
    try { showToast('Замер появится после первой загрузки каталога', 'info', 3000); } catch (e) { /* */ }
    return null;
  }
  // [10.19.6] Версия — в первой строке замера. Причина простая: по снимку
  // экрана нельзя было понять, какая сборка запущена, и мы потеряли на этом
  // целый круг переписки — правки были сделаны, а на экране оставалась старая
  // программа. Теперь версия уезжает вместе с текстом и в буфер обмена.
  const версия = (typeof __APP_VERSION__ !== 'undefined') ? __APP_VERSION__ : '';
  // [10.19.34] Рядом с версией сборки — поколение ТЕКСТОВ замера: владелец
  // поймал день, когда «версия 10.19.33» на экране говорила старыми словами
  // (его ветка и моя носили один номер). Теперь подмену видно по скрину.
  const текст = (версия ? `версия ${версия} · ` : '') + `тексты ${ПОКОЛЕНИЕ_ФОРМУЛИРОВОК} · ` + з.текст;
  const сверка = з.сверка;
      try {
        document.getElementById('время-загрузки')?.remove();
        const w = document.createElement('div');
        w.id = 'время-загрузки';
        w.style.cssText = 'position:fixed;left:8px;right:8px;bottom:8px;z-index:40000;background:#12161f;border:1px solid rgba(150,180,225,0.5);border-radius:12px;padding:14px;color:#dbe6f5;font-size:13px;line-height:1.45;box-shadow:0 10px 40px rgba(0,0,0,0.7);';
        const t = document.createElement('div');
        t.style.cssText = 'user-select:text;-webkit-user-select:text;margin-bottom:10px;';
        t.textContent = 'Время загрузки: ' + текст;
        // Строку сверки готовим здесь, а вставляем в окно ниже, ПОСЛЕ t.
        // Первая попытка звала t.after(св) до того, как t попал в документ, —
        // у элемента без родителя after() молча ничего не делает, и строки на
        // экране не появлялось. Поймано проверкой, а не глазами.
        let св = null;
        if (сверка) {
          св = document.createElement('div');
          св.id = 'сверка-срезов';
          св.style.cssText = 'user-select:text;-webkit-user-select:text;margin-bottom:10px;padding:8px 10px;border-radius:8px;font-weight:600;'
            + (сверка.плохо
              ? 'background:rgba(120,20,20,0.55);border:1px solid #e66;color:#ffd9d9;'
              : 'background:rgba(20,70,40,0.45);border:1px solid #4a8;color:#cfeedd;');
          св.textContent = сверка.текст;
        }
        // [10.19.23] Строки этапов — на экран. Ради них замер и затевался:
        // «медленно» без разбивки не лечится, а «обход 214 с, чтение 41 мс на
        // файл, повторных чтений 92%» указывает на причину прямо.
        let эт = null;
        if (Array.isArray(з.этапы) && з.этапы.length) {
          эт = document.createElement('div');
          эт.id = 'этапы-загрузки';
          эт.style.cssText = 'user-select:text;-webkit-user-select:text;margin-bottom:10px;padding:8px 10px;'
            + 'border-radius:8px;background:rgba(20,32,52,0.85);border:1px solid rgba(120,160,215,0.4);color:#cfe0f5;font-size:12px;';
          эт.textContent = з.этапы.join('\n');
          эт.style.whiteSpace = 'pre-line';
        }
        const row = document.createElement('div');
        row.style.cssText = 'display:flex;gap:8px;';
        const copy = document.createElement('button');
        copy.textContent = 'Скопировать';
        copy.style.cssText = 'flex:1;padding:10px;border-radius:8px;border:1px solid rgba(150,180,225,0.5);background:rgba(30,42,64,0.95);color:#e6eefb;font-size:13px;';
        copy.addEventListener('click', () => {
          try {
            const всёТекстом = '[Время] ' + текст
              + (Array.isArray(з.этапы) && з.этапы.length ? '\n[Этапы] ' + з.этапы.join('\n[Этапы] ') : '')
              + (сверка ? '\n[Сверка] ' + сверка.текст : '');
            navigator.clipboard.writeText(всёТекстом); copy.textContent = 'Скопировано';
          }
          catch (e) { copy.textContent = 'Выделите текст вручную'; }
        });
        const ok = document.createElement('button');
        ok.textContent = 'Закрыть';
        ok.style.cssText = copy.style.cssText;
        ok.addEventListener('click', () => w.remove());
        row.appendChild(copy); row.appendChild(ok);
        w.appendChild(t);
        if (св) w.appendChild(св);
        if (эт) w.appendChild(эт);
        w.appendChild(row);
        document.body.appendChild(w);
        return w;
      } catch (e) { return null; }
  return null;
}
